/**
 * The one z-index scale for both apps and every shared package.
 *
 * Single source of truth: consumed by `theme.extend.zIndex` in
 * apps/dmc/tailwind.config.js and apps/to/tailwind.config.js (which generate
 * the `z-<token>` utilities), and imported directly as `import { zIndex } from
 * '@koob/ui'` wherever a number is unavoidable (react-modal inline overlay
 * styles, styled-components, third-party containers).
 *
 * Rules:
 * 1. Tiers are ordered and spaced so a new layer can be slotted between two
 *    existing ones without renumbering anything.
 * 2. Everything that can open from INSIDE a modal — select menus, date
 *    pickers, dropdown menus, flyouts, tooltips, toasts — sits strictly ABOVE
 *    the `modal` tier. Two portalled layers on the same tier are decided by
 *    DOM insertion order, which is a coin flip; that is the bug class this
 *    scale exists to make impossible.
 * 3. Never hardcode a number. `npm run lint` fails the build on raw `z-<n>`
 *    Tailwind classes, `zIndex: <n>` style properties and `z-index: <n>` in
 *    styled-components (see .eslintrc.zindex.js).
 *
 * Sub-10 raw values are still allowed inside a single component's own
 * stacking context (e.g. `z-index: 1` on a spinner's backdrop): they never
 * compete with anything outside their own subtree.
 */
const zIndex = {
  /** Decorative layers pushed under their own content (blurred bg images). */
  behind: -10,
  /** Explicit "no elevation" — resets a tier without dropping the property. */
  base: 0,
  /** In-card / in-row layering: badges, hover scrims, sticky table cells. */
  raised: 10,
  /** Local loading or disabled overlays covering one card or section. */
  overlay: 20,
  /** Sticky in-page chrome: table headers, editor toolbars, filter bars. */
  sticky: 30,
  /** Non-modal side panels (KoobDrawer) and their scrim: over the page,
   *  under the app sidebar, which stays usable. */
  drawer: 50,
  /** App sidebar and its backdrop. */
  sidebar: 100,
  /** App navbar / top bar — above the sidebar it overlaps. */
  navbar: 200,
  /** Non-modal floating windows (the factsheet assistant): over the app
   *  chrome, since a phone shows them full screen; under modals. */
  floating: 250,
  /** Modal scrim. Same tier as the modal, always painted before it. */
  'modal-backdrop': 300,
  /** Modal / dialog / slide-over panels (headlessui Dialog and react-modal). */
  modal: 310,
  /** Anything portalled from a trigger: selects, date pickers, menus,
   *  popovers, flyouts, dropdowns. MUST outrank `modal`. */
  popover: 400,
  /** Tooltips — above popovers, since a popover's contents can have one. */
  tooltip: 500,
  /** Toasts / snacks. Always the top layer, never blocked by anything. */
  snack: 600,
};

module.exports = { zIndex };
